// 세금계산서 발급 완료 모달 컴포넌트

import { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { EmailSendModal } from './EmailSendModal';
import {
  type InvoiceCompleteModalProps,
  InvoiceSuccessHeader,
  InvoiceDetails,
  InvoiceNotice,
  InvoiceActions,
  invoiceTranslations
} from './invoice';
import { useInvoiceDocument } from '@/hooks';
import { useLanguage } from '@/contexts/AppContext';

export function InvoiceCompleteModal({
  isOpen,
  onClose,
  invoiceData,
}: InvoiceCompleteModalProps) {
  // #522: t lookup 은 15 locale 전수 — language 는 useInvoiceDocument(Language 시그니처) 전용으로 유지.
  const { language, locale } = useLanguage();
  const [notification, setNotification] = useState<string>('');
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);

  const t = invoiceTranslations[locale];

  // 등록된 이메일 (임시 데이터 - 실제로는 사용자 프로필에서 가져와야 함)
  const registeredEmail = 'user@samsung.com';

  // 알림 표시 핸들러
  const showNotification = useCallback((message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  }, []);

  // 세금계산서 문서 생성 훅
  const {
    pdfDownloading,
    pdfSuccess,
    handleDownloadPDF,
    xmlDownloading,
    xmlSuccess,
    handleDownloadXML,
    handleWhatsAppShare,
    handleCopyLink,
    handleViewOnline,
    handleEmailSend
  } = useInvoiceDocument({
    invoiceData,
    language,
    onNotification: showNotification
  });

  // 이메일 모달 열기
  const handleEmailClick = useCallback(() => {
    setShowEmailModal(true);
  }, []);

  // 이메일 모달 닫기
  const handleEmailModalClose = useCallback(() => {
    setShowEmailModal(false);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-background rounded-2xl w-full max-w-md mx-auto shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* 헤더 */}
          <InvoiceSuccessHeader translations={t} />

          {/* 콘텐츠 */}
          <div className="p-6 space-y-4">
            {/* 상세 정보 */}
            <InvoiceDetails
              invoiceData={invoiceData}
              translations={t}
            />

            {/* 중요 알림 */}
            <InvoiceNotice translations={t} />

            {/* 다운로드/공유 액션 */}
            <InvoiceActions
              translations={t}
              pdfDownloading={pdfDownloading}
              pdfSuccess={pdfSuccess}
              onDownloadPDF={handleDownloadPDF}
              xmlDownloading={xmlDownloading}
              xmlSuccess={xmlSuccess}
              onDownloadXML={handleDownloadXML}
              onEmailClick={handleEmailClick}
              onWhatsAppShare={handleWhatsAppShare}
              onCopyLink={handleCopyLink}
              onViewOnline={handleViewOnline}
            />

            {/* 알림 */}
            {notification && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 animate-in fade-in slide-in-from-top-2">
                <p className="text-sm text-green-800 dark:text-green-200 text-center">
                  {notification}
                </p>
              </div>
            )}
          </div>

          {/* 푸터 */}
          <div className="p-6 border-t border-border">
            <Button
              onClick={onClose}
              className="w-full h-[50px] rounded-[5px] bg-primary hover:bg-primary-hover text-white"
            >
              {t.done}
            </Button>
          </div>
        </div>
      </div>

      {/* 이메일 발송 모달 */}
      <EmailSendModal
        isOpen={showEmailModal}
        onClose={handleEmailModalClose}
        onSendEmail={handleEmailSend}
        registeredEmail={registeredEmail}
      />
    </>
  );
}
